var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
const allTypes = ['audioInput', 'micInput'];
const allowedTypes = {
    mics: allTypes,
    micsMuteStatus: allTypes,
    amplifier: allTypes,
    assistiveListeningMute: allTypes,
    assistiveListeningMuteStatus: allTypes,
    assistiveListeningVolume: allTypes,
    audioInputMute: allTypes,
    audioInputMuteStatus: allTypes,
    audioInputVolume: allTypes,
    overflowRoomMute: allTypes,
    overflowRoomMuteStatus: allTypes,
    overflowRoomVolume: allTypes,
    soundReinforcementMute: allTypes,
    soundReinforcementMuteStatus: allTypes,
    soundReinforcementVolume: allTypes,
    telephony: [],
    receiveGain: [],
    preset: [],
};
export class AlfatronSck41 {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.channels = [];
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.channels.map(channel => ({
                    filters: Object.fromEntries(Object.entries(allowedTypes).map(([key, types]) => [
                        key,
                        types.includes(channel.type),
                    ])),
                    name: channel.name,
                    channel: channel.key,
                    type: channel.type,
                }));
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
            powerOffRestrictions: ['get'],
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.getChannel(key);
                if (channel === undefined) {
                    this.log.error(`no channel for key ${key}`);
                    return false;
                }
                const cmdName = key === 'micInput' ? 'MicAudioMute' : 'SourceAudioMute';
                const match = yield this.socket.send(`>Get${cmdName}`, new RegExp(`<${cmdName}\\s+(True|False)`, 'i'), tags);
                return match[1].toLowerCase() === 'true';
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.getChannel(key);
                if (channel === undefined) {
                    this.log.error(`no channel for key ${key}`);
                    return value;
                }
                const cmdName = key === 'micInput' ? 'MicAudioMute' : 'SourceAudioMute';
                const match = yield this.socket.send(`>Set${cmdName} ${value ? 'EN' : 'Dis'}`, new RegExp(`<${cmdName}\\s+(True|False)`, 'i'), tags);
                return match[1].toLowerCase() === 'true';
            }),
            asyncHandlers: [
                {
                    regExp: /<(Mic|Source)AudioMute (True|False)/i,
                    callback: (match) => {
                        this.state.mutes.update(`${match[1].toLowerCase()}Input`, match[2].toLowerCase() === 'true');
                    },
                },
            ],
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding,
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send('>GetPowerOn', /<PowerOn\s+(True|False)/i, tags);
                return match[1].toLowerCase() === 'true';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`>SetPowerOn ${power ? 'EN' : 'Dis'}`, // do not change capitalization
                /<PowerOn\s+(True|False)/i, tags);
                return match[1].toLowerCase() === 'true';
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`>GetAV ${key}`, /<(?:Video|AV)\s*(\d),\s*[A-D]/i, tags);
                return match[1];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`>SetAV ${value},${key}`, /<AV\s*(\d),\s*[A-D]/i, tags);
                return match[1];
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.getChannel(key);
                if (channel === undefined) {
                    this.log.error(`no channel for key ${key}`);
                    return 0;
                }
                const cmdName = key === 'micInput' ? 'MicVOL' : 'SourceVOL';
                const match = yield this.socket.send(`>Get${cmdName}`, new RegExp(`${cmdName}\\s+(\\d+)`, 'i'), tags);
                return parseInt(match[1], 10);
            }),
            set: (key, volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const channel = this.getChannel(key);
                if (channel === undefined) {
                    this.log.error(`no channel for key ${key}`);
                    return 0;
                }
                const cmdName = key === 'micInput' ? 'MicVOL' : 'SourceVOL';
                const match = yield this.socket.send(`>Set${cmdName} ${volume}`, new RegExp(`${cmdName}\\s+(\\d+)`, 'i'), tags);
                return parseInt(match[1], 10);
            }),
            asyncHandlers: [
                {
                    regExp: /<(Mic|Source)VOL\s+(\d+)/i,
                    callback: (match) => {
                        this.state.volumes.update(`${match[1].toLowerCase()}Input`, parseInt(match[2], 10));
                    },
                },
            ],
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 60, min: 36, round: 1 };
                return { max: 60, min: 10, round: 1 };
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                controls: ['power', 'videoMatrixRoutes'],
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.channels.length = 0;
            // Add audio channels. Since there is only one of each type no index is added to the key.
            this.channels.push({
                name: 'Audio Input',
                key: 'audioInput',
                type: 'audioInput',
            });
            this.channels.push({
                name: 'Mic Input',
                key: 'micInput',
                type: 'micInput',
            });
            // Configure auto switching.
            yield this.socket.send(`>SetAutoSwitch ${(config === null || config === void 0 ? void 0 : config.autoSwitchVideoInputs) ? 'EN' : 'Dis'}`, /<AutoSwitch\s*(True|False)/i, ['configure']);
            const match = yield this.socket.send(`>GetStatus`, /<V([0-9a-f\.]+)/i, ['firmware']);
            this.state.firmwareVersion.update(match[1]);
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    send: '>GetPowerOn',
                    next: /<PowerOn\s*(True|False)/i,
                },
            ], credentials);
        });
    }
    // other class methods
    getChannel(key) {
        return this.channels.find(channel => channel.key === key);
    }
    getKey(config) {
        if (typeof config === 'string')
            return config;
        this.log.error(`invalid config: ${JSON.stringify(config)}`);
        return 'invalid';
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            const channel = this.getChannel(key);
            if (channel === undefined)
                return 'missing';
            if (!allowedTypes[info.filter].includes(channel.type))
                return 'incompatible';
            return 'ok';
        });
    }
}
// static properties
AlfatronSck41.channelsConfigName = 'alfatronVideoSwitcherControlChannels';
AlfatronSck41.driverType = 'socketDriver';
AlfatronSck41.key = '-O0AV5n6rhqNuY2MeyXf';
AlfatronSck41.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r', '\n'],
    },
    sendTerminator: '\r\n',
};
AlfatronSck41.socketOptions = [
    Object.assign(Object.assign({}, AlfatronSck41.baseSocketOptions), { port: 4001 }),
    Object.assign(Object.assign({}, AlfatronSck41.baseSocketOptions), { port: 4999 }),
];
